/**
 * Theme Context Provider
 *
 * Provides theme switching functionality between light and dark modes
 * with system preference detection and localStorage persistence.
 *
 * @author GitHub Copilot (Theme System - E12)
 */

import { useEffect, useState, useCallback } from "react";
import { ThemeContext } from "./ThemeContextDefinition.js";
import {
  THEMES,
  THEME_STORAGE_KEY,
  getSystemTheme,
  getStoredTheme,
  applyTheme,
} from "./theme-utils";

// ThemeContext imported from ThemeContextDefinition

/**
 * Theme Provider Component
 */
export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(THEMES.SYSTEM);
  const [systemTheme, setSystemTheme] = useState("dark");

  // Calculate effective theme (resolve 'system' to actual theme)
  const effectiveTheme = theme === THEMES.SYSTEM ? systemTheme : theme;
  const isSystemTheme = theme === THEMES.SYSTEM;

  // Initialize theme on mount
  useEffect(() => {
    const storedTheme = getStoredTheme();
    const currentSystemTheme = getSystemTheme();

    setThemeState(storedTheme);
    setSystemTheme(currentSystemTheme);

    // Apply theme immediately to prevent flash
    const themeToApply =
      storedTheme === THEMES.SYSTEM ? currentSystemTheme : storedTheme;
    applyTheme(themeToApply);
  }, []);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: light)");

    const handleChange = (e) => {
      const newSystemTheme = e.matches ? "light" : "dark";
      setSystemTheme(newSystemTheme);

      // If using system theme, apply the new system theme immediately
      if (theme === THEMES.SYSTEM) {
        applyTheme(newSystemTheme);
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  // Apply theme when effective theme changes
  useEffect(() => {
    applyTheme(effectiveTheme);
  }, [effectiveTheme]);

  // Set theme with persistence
  const setTheme = useCallback((newTheme) => {
    setThemeState(newTheme);

    try {
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    } catch (error) {
      console.warn("Failed to save theme to localStorage:", error);
    }
  }, []);

  // Toggle between light and dark (ignores system)
  const toggleTheme = useCallback(() => {
    const newTheme = effectiveTheme === "light" ? THEMES.DARK : THEMES.LIGHT;
    setTheme(newTheme);
  }, [effectiveTheme, setTheme]);

  const value = {
    theme,
    effectiveTheme,
    setTheme,
    toggleTheme,
    isSystemTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

/**
 * Hook to use theme context
 */
// useTheme is exported from ThemeContextDefinition

// Re-export theme constants for external use
// Note: THEMES is available from './theme-utils'
